import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTER_WORKLOAD_EVENT_METRICS_QUERY_KEY =
  'cluster-workload-event-metrics';

type WorkloadEventMetricsQueryParams = {
  clusterId?: string;
  startTime?: string;
  endTime?: string;
  stepSeconds?: number;
  eventTypes?: string[];
  groupByManagedBy?: boolean;
  /**
   * When set, returns the workload breakdown for only this single aggregation
   * bucket (`items[].byWorkload`).
   */
  bucketTimestamp?: string;
  key: string;
  enabled?: boolean;
};

const getWorkloadEventMetricsQueryKey = ({
  clusterId,
  key,
  eventTypes,
  groupByManagedBy,
  bucketTimestamp,
}: Pick<
  WorkloadEventMetricsQueryParams,
  'clusterId' | 'key' | 'eventTypes' | 'groupByManagedBy' | 'bucketTimestamp'
>) =>
  [
    CLUSTER_WORKLOAD_EVENT_METRICS_QUERY_KEY,
    clusterId,
    key,
    eventTypes,
    groupByManagedBy,
    bucketTimestamp,
  ] as const;

export const getWorkloadEventMetricsQueryOptions = ({
  key,
  clusterId,
  enabled,
  endTime,
  eventTypes,
  groupByManagedBy,
  bucketTimestamp,
  startTime,
  stepSeconds,
}: WorkloadEventMetricsQueryParams) =>
  queryOptions({
    queryKey: getWorkloadEventMetricsQueryKey({
      clusterId,
      key,
      eventTypes,
      groupByManagedBy,
      bucketTimestamp,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getClusterWorkloadEventMetrics({
          clusterId: clusterId!,
          startTime: startTime!,
          endTime: endTime!,
          stepSeconds,
          eventTypes,
          groupByManagedBy,
          bucketTimestamp,
        });
      return data?.series;
    },
    enabled: makeIsEnabled(!!clusterId && !!startTime && !!endTime, enabled),
  });

export const useWorkloadEventMetricsQuery = (
  params: WorkloadEventMetricsQueryParams
) => useQuery(getWorkloadEventMetricsQueryOptions(params));
